/**
 * HouseholdSwitcher — picker for users who can see multiple households.
 *
 * IFA use case: an advisor with several clients gets a dropdown to flip
 * between them. Hidden for users who only have one household (the default).
 * Hidden for demo users (they're pinned).
 *
 * Switching calls POST /api/households/switch which updates the user's
 * active `portfolio_owner_id` on the backend, then we reload so every
 * page re-fetches data for the new household.
 */
import { useEffect, useState } from 'react'
import { Users, Check, ChevronDown } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

interface AccessibleHousehold {
  household_id: string
  nickname: string | null
  role: string
  account_count: number
  total_value_gbp: number
  is_active: boolean
  is_home: boolean
}

function fmtShort(n: number): string {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`
  return `£${n.toFixed(0)}`
}

export default function HouseholdSwitcher() {
  const user = useAuth(s => s.user)
  const [households, setHouseholds] = useState<AccessibleHousehold[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState<string | null>(null)

  const isDemo = user?.permissions?.can_configure === false

  const fetchList = async () => {
    if (!user || isDemo) return
    setLoading(true)
    try {
      const { data } = await api.get<AccessibleHousehold[]>('/api/households/accessible')
      setHouseholds(Array.isArray(data) ? data : [])
    } catch {
      setHouseholds([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const activeHousehold = households.find(h => h.is_active)

  // Hide the switcher for users with only one household — no point showing
  // a dropdown with one entry.
  if (isDemo || households.length <= 1) return null

  const label = (h: AccessibleHousehold) =>
    h.nickname || h.household_id.replace(/_/g, ' ')

  const onSwitch = async (target: string) => {
    if (target === activeHousehold?.household_id) {
      setOpen(false)
      return
    }
    setSwitching(target)
    try {
      await api.post('/api/households/switch', { household_id: target })
      // Full reload so every cached query re-fetches for the new household
      window.location.reload()
    } catch (err) {
      setSwitching(null)
      setOpen(false)
    }
  }

  return (
    <div className="px-3 py-2 border-t border-border">
      <p className="panel-title text-[9px] mb-1.5">
        Active Household
      </p>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          disabled={loading || switching !== null}
          className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-sm
                     bg-surface-card border border-border hover:bg-surface-hover hover:border-border-light
                     transition-colors focus-visible:ring-2 focus-visible:ring-blue
                     disabled:opacity-60 disabled:cursor-wait"
          aria-label="Switch household"
          aria-expanded={open}
        >
          <Users size={14} className="text-blue-light shrink-0" aria-hidden />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-text-primary truncate">
              {activeHousehold ? label(activeHousehold) : 'Select…'}
            </p>
            {activeHousehold && (
              <p className="text-[10px] text-text-muted truncate">
                {activeHousehold.account_count} acct · {fmtShort(activeHousehold.total_value_gbp)}
              </p>
            )}
          </div>
          <ChevronDown size={12} className="text-text-muted shrink-0" aria-hidden />
        </button>

        {open && (
          <div
            className="card absolute bottom-full left-0 right-0 mb-1
                       shadow-lg overflow-hidden z-10 max-h-72 overflow-y-auto"
            role="listbox"
          >
            {households.map(h => (
              <button
                key={h.household_id}
                type="button"
                onClick={() => onSwitch(h.household_id)}
                disabled={switching !== null}
                className={clsx(
                  'w-full flex items-center gap-2 px-2.5 py-2 text-left text-sm transition-colors',
                  'hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none',
                  h.is_active && 'bg-blue/5',
                  switching === h.household_id && 'opacity-60',
                )}
                role="option"
                aria-selected={h.is_active}
              >
                <div className="w-4 shrink-0">
                  {h.is_active && <Check size={12} className="text-emerald" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-xs font-semibold text-text-primary truncate">
                      {label(h)}
                    </p>
                    {h.is_home && (
                      <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 rounded bg-blue/10 text-blue-light border border-blue/20">
                        home
                      </span>
                    )}
                    {h.role !== 'owner' && (
                      <span className="text-[8px] uppercase tracking-wider text-text-muted">
                        {h.role}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-text-muted truncate">
                    {h.account_count} acct · {fmtShort(h.total_value_gbp)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
